import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Text, Card, Button } from '@hawkins/components';
import { useTranslation } from 'react-i18next';
import NetflixGradientBackground from 'src/components/elements/NetflixGradientBackground';
import { AnnouncementBanner } from 'src/components/elements/AnnouncementBanner';

const NetflixLogo = () => (
  <div style={{ marginBottom: '40px' }}>
    <img 
      src="/nflx-logo-classic@150w.png" 
      alt="Netflix" 
      style={{ height: '80px' }}
    />
  </div>
);

const ErrorDisplay = ({ error }: { error: string }) => (
  <Card style={{
    backgroundColor: '#660000',
    border: '1px solid #cc0000',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '20px'
  }}>
    <Text style={{ color: '#ffcccc', fontSize: '18px', textAlign: 'center' }}>
      {error}
    </Text>
  </Card>
);

// Base64 decode state parameter to extract reset token
const decodeState = (state: string): { resetToken: string; csrf: string } => {
  try {
    const decoded = atob(state);
    const [resetToken, csrf] = decoded.split(':');
    return { resetToken, csrf };
  } catch (error) {
    throw new Error('Invalid state parameter');
  }
};

function ResetMfaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resetToken, setResetToken] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string>('');

  const exchangeTokensAndRedirect = useCallback(async (authorizationCode: string, state: string, tokenForRedirect: string) => {
    setLoading(true);
    
    try {
      const redirectUri = `${window.location.origin}/mfa`;
      
      const response = await fetch('/api/mfa/exchange-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authorizationCode,
          state,
          redirectUri
        }),
        credentials: 'include' // Include cookies
      });
      
      const result = await response.json();
      
      if (result.success && result.identitiesMatch) {
        // MFA completed successfully, redirect to password form
        navigate(`/reset/${tokenForRedirect}/password-form`);
      } else {
        setAuthError(result.error || 'MFA authentication failed');
      }
    } catch (error) {
      setAuthError('Failed to complete MFA authentication');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Check URL for OAuth callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (code && state) {
      // Extract reset token from state parameter (OAuth callback)
      try {
        const { resetToken: tokenFromState } = decodeState(state);
        setResetToken(tokenFromState);
        
        // Clean URL without triggering navigation
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Exchange tokens and redirect back to password form
        exchangeTokensAndRedirect(code, state, tokenFromState);
      } catch (error) {
        setAuthError('Invalid authentication state');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else if (error) {
      setAuthError(errorDescription || error);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Get reset token from URL params (initial load)
      const tokenFromParams = searchParams.get('resetToken');
      if (tokenFromParams) {
        setResetToken(tokenFromParams);
      } else {
        setAuthError('Missing reset token parameter');
      }
    }
  }, [searchParams, exchangeTokensAndRedirect]);

  const initiateMfa = async () => {
    if (!resetToken) return;
    
    setLoading(true);
    setAuthError('');
    
    try {
      const redirectUri = `${window.location.origin}/mfa`;
      
      // Call backend to generate secure OAuth URL
      const response = await fetch('/api/mfa/initiate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resetToken: resetToken,
          redirectUri: redirectUri
        }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'MFA initiation failed');
      }
      
      // Redirect to IDP with backend-generated secure URL
      window.location.href = result.authorizationUrl;
      
    } catch (error) {
      setAuthError(`Failed to initiate MFA: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setLoading(false);
    }
  };


  return (
    <NetflixGradientBackground>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        paddingTop: 'clamp(80px, 15vh, 120px)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '450px'
        }}>
          <NetflixLogo />

          <Card style={{
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            padding: '48px 68px 36px',
            borderRadius: '4px',
            width: '100%',
            border: 'none'
          }}>
            <Text 
              style={{ 
                color: 'white', 
                fontSize: '32px',
                fontWeight: '700',
                marginBottom: '24px',
                textAlign: 'center'
              }}
            >
              {t('auth.mfa.title')}
            </Text>

            <Text style={{ 
              color: '#999', 
              fontSize: '16px', 
              marginBottom: '32px',
              textAlign: 'center',
              lineHeight: '1.5'
            }}>
              {t('auth.mfa.description')}
            </Text>

            {authError && (
              <ErrorDisplay error={authError} />
            )}

            <Button
              onClick={initiateMfa}
              disabled={loading || !resetToken}
              style={{
                width: '100%',
                backgroundColor: loading ? '#666' : '#e50914',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                padding: '12px 16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                height: '48px',
                minHeight: '48px',
                boxSizing: 'border-box',
                marginBottom: '20px'
              }}
            >
              {loading ? t('auth.mfa.processing') : t('auth.mfa.continueButton')}
            </Button>

            <Text style={{ 
              color: '#b3b3b3', 
              fontSize: '14px', 
              textAlign: 'center',
              lineHeight: '1.4'
            }}>
              {t('auth.mfa.helpText')}
            </Text>
          </Card>
        </div>
      </div>

      <AnnouncementBanner enabled={false} />
    </NetflixGradientBackground>
  );
}

export default ResetMfaPage;